import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtGNF, fmtDate, openAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CreditCard, FileText, CheckCircle2, Clock, Plus, Loader2, Banknote, Building, Smartphone, ExternalLink, QrCode } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const METHOD_ICONS = { cash: Banknote, stripe: CreditCard, wire_transfer: Building, mobile_money: Smartphone };
const METHOD_LABELS = { cash: "Espèces", stripe: "Carte (Stripe)", wire_transfer: "Virement bancaire", mobile_money: "Mobile Money" };

export default function PaymentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [form, setForm] = useState({ student_id: "", amount: "", type: "tuition", method: "wire_transfer", period: "", notes: "" });
  // Mobile Money (CinetPay)
  const [mmOpen, setMmOpen] = useState(false);
  const [mmPayment, setMmPayment] = useState(null);
  const [mmForm, setMmForm] = useState({ provider: "ALL", phone: "" });
  const [mmLoading, setMmLoading] = useState(false);
  const [mmDemoTx, setMmDemoTx] = useState(null);
  const [qrModal, setQrModal] = useState({ open: false, paymentId: null, amount: 0 });

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/payments"), api.get("/students")]);
    setPayments(p.data);
    setStudents(s.data);
  };
  useEffect(() => { load(); }, []);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const canceled = searchParams.get("canceled");
    if (canceled) {
      toast.info("Paiement annulé. Vous pouvez réessayer.");
      setSearchParams({}, { replace: true });
      return;
    }
    if (!sessionId) return;

    toast.loading("Vérification du paiement en cours...", { id: "stripe-poll" });
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success(`Paiement réussi de ${fmtGNF(data.amount_gnf)} ! Reçu disponible en PDF.`, { id: "stripe-poll" });
          setSearchParams({}, { replace: true });
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("La session Stripe a expiré. Veuillez réessayer.", { id: "stripe-poll" });
          setSearchParams({}, { replace: true });
          return;
        }
        if (attempts >= maxAttempts) {
          toast.error("Délai dépassé. Vérifiez votre email ou actualisez la page.", { id: "stripe-poll" });
          setSearchParams({}, { replace: true });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        toast.error("Erreur lors de la vérification du paiement.", { id: "stripe-poll" });
        setSearchParams({}, { replace: true });
      }
    };
    poll();
  }, []);

  // Handle return from CinetPay (live or demo)
  useEffect(() => {
    const tx = searchParams.get("cinetpay_tx");
    const demo = searchParams.get("cinetpay_demo");
    if (!tx) return;
    if (demo === "1") {
      // DEMO mode: show confirm button
      setMmDemoTx(tx);
      return;
    }
    // LIVE mode: poll status
    toast.loading("Vérification du paiement Mobile Money...", { id: "cp-poll" });
    let attempts = 0;
    const maxAttempts = 12;
    const pollMM = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/cinetpay/status/${tx}`);
        if (data.payment_status === "paid") {
          toast.success(`Paiement Mobile Money réussi de ${fmtGNF(data.amount_gnf)} !`, { id: "cp-poll" });
          setSearchParams({}, { replace: true });
          load();
          return;
        }
        if (data.payment_status === "failed") {
          toast.error("Paiement Mobile Money refusé.", { id: "cp-poll" });
          setSearchParams({}, { replace: true });
          return;
        }
        if (attempts >= maxAttempts) {
          toast.info("Le paiement est toujours en attente. Actualisez plus tard.", { id: "cp-poll" });
          setSearchParams({}, { replace: true });
          return;
        }
        setTimeout(pollMM, 3000);
      } catch {
        toast.error("Erreur de vérification CinetPay.", { id: "cp-poll" });
        setSearchParams({}, { replace: true });
      }
    };
    pollMM();
  }, []);

  const confirmDemoMM = async () => {
    if (!mmDemoTx) return;
    try {
      await api.post(`/payments/cinetpay/demo-confirm/${mmDemoTx}`);
      toast.success("Paiement Mobile Money confirmé (DÉMO).");
      setMmDemoTx(null);
      setSearchParams({}, { replace: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de confirmation");
    }
  };

  const studentMap = Object.fromEntries(students.map(s => [s.id, s]));

  const viewReceipt = async (id) => {
    try {
      await openAuthedFile(`/payments/${id}/receipt`);
    } catch { toast.error("Impossible d'ouvrir le reçu PDF"); }
  };

  const submit = async () => {
    if (!form.student_id || !form.amount) {
      toast.error("Veuillez sélectionner un enfant et saisir le montant");
      return;
    }
    setLoading(true);
    try {
      await api.post("/payments", { ...form, amount: parseFloat(form.amount) });
      toast.success("Paiement enregistré. En attente de validation par le service financier.");
      setOpen(false);
      setForm({ student_id: "", amount: "", type: "tuition", method: "wire_transfer", period: "", notes: "" });
      load();
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally { setLoading(false); }
  };

  const payByStripe = async (payment) => {
    setPayingId(payment.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        payment_id: payment.id,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'initialisation du paiement Stripe");
      setPayingId(null);
    }
  };

  const openMobileMoney = (payment) => {
    setMmPayment(payment);
    setMmForm({ provider: "ALL", phone: "" });
    setMmOpen(true);
  };

  const submitMobileMoney = async () => {
    if (!mmForm.phone || mmForm.phone.length < 8) {
      toast.error("Numéro de téléphone invalide");
      return;
    }
    setMmLoading(true);
    try {
      const { data } = await api.post("/payments/cinetpay/initiate", {
        payment_id: mmPayment.id,
        origin_url: window.location.origin,
        customer_phone: mmForm.phone,
        provider: mmForm.provider,
      });
      if (data.mode === "demo") {
        toast.info(data.message || "Mode DÉMO activé.");
      }
      setMmOpen(false);
      window.location.href = data.payment_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur Mobile Money");
    } finally {
      setMmLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="page-payments">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Mes paiements</h2>
        <div className="flex gap-2">
          <WhatsAppButton
            message="Bonjour, j'ai une question concernant les paiements de scolarité."
            label="Aide WhatsApp"
            testId="payments-whatsapp"
          />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="new-payment-btn">
                <Plus className="h-4 w-4" /> Déclarer un paiement
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Déclarer un paiement</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Enfant *</Label>
                  <Select value={form.student_id} onValueChange={(v) => setForm({...form, student_id: v})}>
                    <SelectTrigger data-testid="pay-student"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>
                      {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} — {s.class_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type *</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                      <SelectTrigger data-testid="pay-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tuition">Scolarité</SelectItem>
                        <SelectItem value="registration">Inscription</SelectItem>
                        <SelectItem value="books">Manuels</SelectItem>
                        <SelectItem value="other">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Mode *</Label>
                    <Select value={form.method} onValueChange={(v) => setForm({...form, method: v})}>
                      <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="wire_transfer">Virement bancaire</SelectItem>
                        <SelectItem value="cash">Espèces (au guichet)</SelectItem>
                        <SelectItem value="stripe">Carte bancaire (Stripe)</SelectItem>
                        <SelectItem value="mobile_money">Mobile Money</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Montant (GNF) *</Label>
                  <Input type="number" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} placeholder="350000" data-testid="pay-amount" />
                </div>
                <div>
                  <Label>Période</Label>
                  <Input value={form.period} onChange={(e) => setForm({...form, period: e.target.value})} placeholder="ex: Octobre 2026" data-testid="pay-period" />
                </div>
                <Button onClick={submit} disabled={loading} className="bg-[#166534] hover:bg-[#14532d] text-white w-full" data-testid="pay-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Déclarer le paiement"}
                </Button>
                <p className="text-xs text-slate-500 text-center">Pour un paiement immédiat, utilisez les boutons <strong>Carte</strong> ou <strong>Mobile Money</strong> directement sur un paiement en attente.</p>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-gradient-to-r from-[#166534]/10 to-[#1E3A8A]/10 border border-[#166534]/20 rounded-xl p-5 flex items-start gap-4">
        <CreditCard className="h-7 w-7 text-[#166534] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-heading font-bold text-[#1E3A8A] mb-1">Paiement sécurisé par carte bancaire</div>
          <p className="text-sm text-slate-600">
            Réglez instantanément vos frais via <strong>Stripe</strong> (cartes Visa, Mastercard, etc.). Le reçu PDF est généré automatiquement après confirmation du paiement. Conversion GNF→USD au taux du jour.
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3 text-left">N° Reçu</th>
                <th className="px-5 py-3 text-left">Date</th>
                <th className="px-5 py-3 text-left">Enfant</th>
                <th className="px-5 py-3 text-left">Type</th>
                <th className="px-5 py-3 text-left">Mode</th>
                <th className="px-5 py-3 text-right">Montant</th>
                <th className="px-5 py-3 text-center">Statut</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.map((p) => {
                const Mi = METHOD_ICONS[p.method] || CreditCard;
                const st = studentMap[p.student_id];
                const isPaying = payingId === p.id;
                return (
                  <tr key={p.id} className="hover:bg-slate-50" data-testid={`payment-row-${p.id}`}>
                    <td className="px-5 py-4 text-sm font-mono text-[#1E3A8A]">{p.receipt_number}</td>
                    <td className="px-5 py-4 text-sm text-slate-600">{fmtDate(p.created_at)}</td>
                    <td className="px-5 py-4 text-sm">{st ? `${st.first_name} ${st.last_name}` : "—"}</td>
                    <td className="px-5 py-4 text-sm">{({registration: "Inscription", tuition: "Scolarité", books: "Manuels", other: "Autre"})[p.type]}</td>
                    <td className="px-5 py-4 text-sm"><div className="flex items-center gap-2"><Mi className="h-4 w-4 text-slate-500" /> {METHOD_LABELS[p.method]}</div></td>
                    <td className="px-5 py-4 text-sm font-semibold text-right">{fmtGNF(p.amount)}</td>
                    <td className="px-5 py-4 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${p.status === "paid" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                        {p.status === "paid" ? <><CheckCircle2 className="h-3 w-3" /> Payé</> : <><Clock className="h-3 w-3" /> En attente</>}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {p.status === "pending" && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => payByStripe(p)}
                              disabled={isPaying}
                              className="bg-[#635BFF] hover:bg-[#5048E5] text-white gap-1 text-xs"
                              data-testid={`stripe-pay-${p.id}`}
                            >
                              {isPaying ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CreditCard className="h-3 w-3" /> Carte</>}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => openMobileMoney(p)}
                              className="bg-[#EA580C] hover:bg-orange-700 text-white gap-1 text-xs"
                              data-testid={`momo-pay-${p.id}`}
                            >
                              <Smartphone className="h-3 w-3" /> Mobile Money
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setQrModal({ open: true, paymentId: p.id, amount: p.amount })}
                              className="text-xs"
                              data-testid={`qr-pay-${p.id}`}
                              title="QR code de paiement"
                            >
                              <QrCode className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {p.status === "paid" && (
                          <Button size="sm" variant="outline" onClick={() => viewReceipt(p.id)} className="text-xs gap-1 border-green-600 text-green-700 hover:bg-green-50" data-testid={`receipt-btn-${p.id}`}>
                            <FileText className="h-3 w-3" /> Reçu officiel PDF
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {payments.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-400">Aucun paiement enregistré</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* DEMO confirmation banner */}
      {mmDemoTx && (
        <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-5 flex items-start gap-4" data-testid="momo-demo-banner">
          <Smartphone className="h-7 w-7 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-bold text-orange-900 mb-1">Mode DÉMO Mobile Money</div>
            <p className="text-sm text-orange-800 mb-3">
              CinetPay n&apos;est pas encore configuré en production. Cliquez sur <strong>Confirmer le paiement</strong> pour simuler un règlement Orange Money / MTN MoMo réussi.
              <br/>Transaction : <code className="font-mono text-xs">{mmDemoTx}</code>
            </p>
            <div className="flex gap-2">
              <Button onClick={confirmDemoMM} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="momo-demo-confirm">
                <CheckCircle2 className="h-4 w-4" /> Confirmer le paiement (DÉMO)
              </Button>
              <Button variant="outline" onClick={() => { setMmDemoTx(null); setSearchParams({}, {replace:true}); }} data-testid="momo-demo-cancel">
                Annuler
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Money Dialog */}
      <Dialog open={mmOpen} onOpenChange={setMmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5 text-[#EA580C]" /> Paiement Mobile Money</DialogTitle>
          </DialogHeader>
          {mmPayment && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="text-xs text-slate-500">Montant à régler</div>
                <div className="text-2xl font-extrabold text-[#1E3A8A]">{fmtGNF(mmPayment.amount)}</div>
                <div className="text-xs text-slate-500 mt-1">Reçu : {mmPayment.receipt_number}</div>
              </div>
              <div>
                <Label>Opérateur</Label>
                <Select value={mmForm.provider} onValueChange={(v) => setMmForm({...mmForm, provider: v})}>
                  <SelectTrigger data-testid="mm-provider"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Au choix sur la page de paiement</SelectItem>
                    <SelectItem value="ORANGE_MONEY">Orange Money Guinée</SelectItem>
                    <SelectItem value="MTN_MOMO">MTN Mobile Money</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Numéro de téléphone (sans indicatif)</Label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-slate-50 text-slate-600 text-sm">+224</span>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    value={mmForm.phone}
                    onChange={(e) => setMmForm({...mmForm, phone: e.target.value.replace(/\D/g, "")})}
                    placeholder="622 66 82 77"
                    className="rounded-l-none"
                    data-testid="mm-phone"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Vous recevrez une notification USSD/PIN sur ce numéro pour confirmer.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMmOpen(false)}>Annuler</Button>
                <Button
                  onClick={submitMobileMoney}
                  disabled={mmLoading}
                  className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2"
                  data-testid="mm-submit"
                >
                  {mmLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Smartphone className="h-4 w-4" /> Payer {fmtGNF(mmPayment.amount)}</>}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* QR Code Dialog */}
      <Dialog open={qrModal.open} onOpenChange={(o) => setQrModal({...qrModal, open: o})}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5" />QR Code de paiement</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            {qrModal.paymentId && (
              <img
                src={`${api.defaults.baseURL}/qr/payment/${qrModal.paymentId}.png`}
                alt="QR code"
                className="w-64 h-64 border border-slate-200 rounded-lg"
                data-testid="qr-image"
              />
            )}
            <div className="text-center">
              <div className="text-xs uppercase font-bold text-slate-500">Montant</div>
              <div className="text-2xl font-extrabold text-[#1E3A8A]">{fmtGNF(qrModal.amount)}</div>
              <p className="text-xs text-slate-500 mt-2">Scannez ce QR code pour ouvrir la page de paiement.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
